/**
 * Splash Screen Theme Calendar
 * ─────────────────────────────
 * Holidays (take priority, checked in order):
 *   New Year's   — Dec 31 +- 2
 *   April Fools  — Apr 1 only
 *   Bastille Day — Jul 14 +- 1 (14 Juillet)
 *   Halloween    — Oct 31 +- 1
 *   Christmas    — Dec 10 – Jan 5
 *   Easter       — 3 days before – 3 days after Easter Sunday (varies yearly)
 *
 * Seasons (first week only):
 *   Winter      — Jan 6 – Jan 19
 *   Spring      — Mar 20 – Mar 26
 *   Summer      — Jun 21 – Jun 27
 *   Autumn      — Sep 23 – Sep 29
 *
 * Default (no particles, original branded splash):
 *   All other dates
 *
 * Use [forceTheme] input to override auto-detection.
 */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

export type SplashTheme =
	| 'default'
	| 'christmas'
	| 'easter'
	| 'halloween'
	| 'bastille-day'
	| 'new-year'
	| 'april-fools'
	| 'spring'
	| 'summer'
	| 'autumn'
	| 'winter';

export interface Particle {
	type: string;
	left: number;
	delay: number;
	duration: number;
	size: number;
	hue: number;
	opacity: number;
}

@Component({
	imports: [CommonModule],
	standalone: true,
	selector: 'app-splash',
	templateUrl: './splash.component.html',
	styleUrl: './splash.component.scss',
})
export class SplashComponent implements OnInit {
	@Input() public forceTheme: SplashTheme | null = null;
	@Output() public dismissed = new EventEmitter<void>();
	public fading = false;
	public readonly subtitleText =
		localStorage.getItem('pref:lang') === 'fr'
			? 'Chargement de l\u2019application\u2026'
			: 'Loading application\u2026';
	public theme: SplashTheme = 'default';
	public particles: Array<Particle> = [];

	public ngOnInit(): void {
		this.theme =
			this.forceTheme ||
			(localStorage.getItem('__splashTheme') as SplashTheme) ||
			this.detectTheme();
		this.particles = this.generateParticles();

		setTimeout(() => {
			this.fading = true;
			setTimeout(() => this.dismissed.emit(), 500);
		}, 2000);
	}

	private generateParticles(): Array<Particle> {
		const config = this.getParticleConfig();
		const result: Array<Particle> = [];

		for (let i = 0; i < config.count; i++) {
			const type = config.types[i % config.types.length];
			result.push({
				type,
				left: (i * 61.803) % 100,
				delay:
					i % 2 === 0
						? (-(config.baseDuration + (i % 6)) * ((i % 10) + 1)) / 11
						: (i * 0.3) % 3,
				duration: config.baseDuration + (i % 6),
				size: this.getSizeForType(type, i),
				hue: (i * 47) % 360,
				opacity: 0.4 + (i % 5) * 0.15,
			});
		}

		return result;
	}

	private getParticleConfig(): {
		types: Array<string>;
		count: number;
		baseDuration: number;
	} {
		switch (this.theme) {
			case 'default':
				return {
					types: [],
					count: 0,
					baseDuration: 0,
				};
			case 'christmas':
				return {
					types: [
						'snowflake',
						'snowflake',
						'snowflake',
						'ornament',
						'candy-cane',
						'star',
						'gift',
						'gingerbread',
					],
					count: 30,
					baseDuration: 4,
				};
			case 'new-year':
				return {
					types: [
						'confetti',
						'confetti',
						'confetti',
						'champagne',
						'party-hat',
						'clock',
						'firework-burst',
					],
					count: 30,
					baseDuration: 4,
				};
			case 'april-fools':
				return {
					types: [
						'jester-hat',
						'question-mark',
						'whoopee',
						'spring-snake',
						'banana-peel',
						'joy-emoji',
						'fish',
						'fish',
					],
					count: 26,
					baseDuration: 5,
				};
			case 'bastille-day':
				return {
					types: [
						'firework',
						'firework',
						'tricolor-flag',
						'rosette',
						'eiffel',
						'fleur-de-lis',
					],
					count: 26,
					baseDuration: 5,
				};
			case 'halloween':
				return {
					types: [
						'jack-o-lantern',
						'jack-o-lantern',
						'ghost',
						'bat',
						'spider',
						'candy-corn',
						'witch-hat',
					],
					count: 28,
					baseDuration: 5,
				};
			case 'easter':
				return {
					types: ['egg', 'egg', 'egg', 'bunny', 'flower', 'butterfly', 'chick'],
					count: 28,
					baseDuration: 5,
				};
			case 'spring':
				return {
					types: [
						'cherry-blossom',
						'cherry-blossom',
						'tulip',
						'butterfly',
						'bee',
						'raindrop',
						'daisy',
						'sunflower',
						'lavender',
					],
					count: 30,
					baseDuration: 5,
				};
			case 'summer':
				return {
					types: [
						'sun',
						'wave',
						'palm-leaf',
						'seashell',
						'ice-cream',
						'beach-ball',
						'beach-ball',
						'surfboard',
						'beach-towel',
						'sunglasses',
					],
					count: 28,
					baseDuration: 5,
				};
			case 'autumn':
				return {
					types: [
						'maple-leaf',
						'maple-leaf',
						'maple-leaf',
						'acorn',
						'mushroom',
						'pumpkin',
					],
					count: 28,
					baseDuration: 5,
				};
			case 'winter':
				return {
					types: [
						'snowflake',
						'snowflake',
						'snowflake',
						'snowflake',
						'icicle',
						'mitten',
						'scarf',
						'skis',
						'gloves',
					],
					count: 30,
					baseDuration: 4,
				};
		}
	}

	private getSizeForType(type: string, index: number): number {
		switch (type) {
			// Christmas
			case 'snowflake':
				return 12 + (index % 5) * 5;
			case 'ornament':
				return 20 + (index % 4) * 5;
			case 'candy-cane':
				return 24 + (index % 3) * 5;
			case 'star':
				return 16 + (index % 4) * 5;
			case 'gift':
				return 22 + (index % 4) * 5;
			case 'gingerbread':
				return 24 + (index % 3) * 6;
			// New Year
			case 'confetti':
				return 8 + (index % 5) * 3;
			case 'champagne':
				return 22 + (index % 3) * 5;
			case 'party-hat':
				return 20 + (index % 4) * 5;
			case 'clock':
				return 22 + (index % 3) * 5;
			case 'firework-burst':
				return 24 + (index % 4) * 5;
			// April Fools
			case 'jester-hat':
				return 22 + (index % 4) * 5;
			case 'question-mark':
				return 16 + (index % 4) * 4;
			case 'whoopee':
				return 20 + (index % 3) * 5;
			case 'spring-snake':
				return 18 + (index % 3) * 5;
			case 'banana-peel':
				return 18 + (index % 3) * 4;
			case 'joy-emoji':
				return 20 + (index % 4) * 4;
			case 'fish':
				return 20 + (index % 4) * 5;
			// Bastille Day
			case 'firework':
				return 20 + (index % 4) * 5;
			case 'tricolor-flag':
				return 22 + (index % 3) * 5;
			case 'rosette':
				return 18 + (index % 4) * 5;
			case 'eiffel':
				return 24 + (index % 3) * 6;
			case 'fleur-de-lis':
				return 18 + (index % 4) * 4;
			// Halloween
			case 'jack-o-lantern':
				return 24 + (index % 4) * 5;
			case 'ghost':
				return 22 + (index % 4) * 6;
			case 'bat':
				return 22 + (index % 3) * 6;
			case 'spider':
				return 16 + (index % 4) * 4;
			case 'candy-corn':
				return 14 + (index % 3) * 4;
			case 'witch-hat':
				return 22 + (index % 3) * 5;
			// Easter
			case 'egg':
				return 20 + (index % 5) * 6;
			case 'bunny':
				return 28 + (index % 4) * 5;
			case 'chick':
				return 20 + (index % 3) * 5;
			// Spring
			case 'cherry-blossom':
				return 16 + (index % 5) * 4;
			case 'tulip':
				return 20 + (index % 4) * 5;
			case 'bee':
				return 16 + (index % 3) * 4;
			case 'raindrop':
				return 8 + (index % 4) * 3;
			case 'daisy':
				return 18 + (index % 4) * 4;
			case 'sunflower':
				return 22 + (index % 3) * 5;
			case 'lavender':
				return 16 + (index % 4) * 5;
			// Summer
			case 'sun':
				return 22 + (index % 4) * 5;
			case 'wave':
				return 24 + (index % 3) * 6;
			case 'palm-leaf':
				return 22 + (index % 4) * 5;
			case 'seashell':
				return 18 + (index % 4) * 4;
			case 'ice-cream':
				return 20 + (index % 3) * 5;
			case 'beach-ball':
				return 22 + (index % 4) * 5;
			case 'surfboard':
				return 24 + (index % 3) * 6;
			case 'beach-towel':
				return 20 + (index % 4) * 5;
			case 'sunglasses':
				return 22 + (index % 4) * 4;
			// Autumn
			case 'maple-leaf':
				return 18 + (index % 5) * 5;
			case 'acorn':
				return 16 + (index % 4) * 4;
			case 'mushroom':
				return 20 + (index % 3) * 5;
			case 'pumpkin':
				return 22 + (index % 4) * 5;
			// Winter
			case 'icicle':
				return 10 + (index % 4) * 4;
			case 'mitten':
				return 20 + (index % 3) * 5;
			case 'scarf':
				return 22 + (index % 3) * 5;
			case 'skis':
				return 24 + (index % 3) * 6;
			case 'gloves':
				return 18 + (index % 4) * 4;
			// Shared
			case 'flower':
				return 18 + (index % 4) * 5;
			case 'butterfly':
				return 16 + (index % 3) * 6;
			default:
				return 20;
		}
	}

	private detectTheme(): SplashTheme {
		const now = new Date();
		const month = now.getMonth();
		const day = now.getDate();

		// New Year's: Dec 30,31 – Jan 1,2
		if (
			(month === 11 && day === 30) ||
			(month === 11 && day === 31) ||
			(month === 0 && day === 1) ||
			(month === 0 && day === 2)
		) {
			return 'new-year';
		}

		// April Fools: Apr 1
		if (month === 3 && day === 1) {
			return 'april-fools';
		}

		// Bastille Day: Jul 13–14-15 (show day before since Jul 14 is a national holiday)
		if (month === 6 && (day === 13 || day === 14 || day === 15)) {
			return 'bastille-day';
		}

		// Halloween: Oct 30-31, Nov 1
		if (
			(month === 9 && (day === 30 || day === 31)) ||
			(month === 10 && day === 1)
		) {
			return 'halloween';
		}

		// Christmas: Dec 10 – Jan 5
		if ((month === 11 && day >= 10) || (month === 0 && day <= 5)) {
			return 'christmas';
		}

		// Easter: 3 days before through 3 days after Easter Sunday
		const easter = this.computeEasterDate(now.getFullYear());
		const start = new Date(easter);
		start.setDate(start.getDate() - 3);
		const end = new Date(easter);
		end.setDate(end.getDate() + 3);

		if (now >= start && now <= end) {
			return 'easter';
		}

		// Seasons — themed only during the first week, otherwise default
		// Spring: Mar 20 – Mar 26
		if (month === 2 && day >= 20 && day <= 26) {
			return 'spring';
		}

		// Summer: Jun 21 – Jun 27
		if (month === 5 && day >= 21 && day <= 27) {
			return 'summer';
		}

		// Autumn: Sep 23 – Sep 29
		if (month === 8 && day >= 23 && day <= 29) {
			return 'autumn';
		}

		// Winter: Jan 6 – Jan 19 (after Christmas window ends)
		if (month === 0 && day >= 6 && day <= 19) {
			return 'winter';
		}

		return 'default';
	}

	// Anonymous Gregorian algorithm
	private computeEasterDate(year: number): Date {
		const a = year % 19;
		const b = Math.floor(year / 100);
		const c = year % 100;
		const d = Math.floor(b / 4);
		const e = b % 4;
		const f = Math.floor((b + 8) / 25);
		const g = Math.floor((b - f + 1) / 3);
		const h = (19 * a + b - d - g + 15) % 30;
		const i = Math.floor(c / 4);
		const k = c % 4;
		const l = (32 + 2 * e + 2 * i - h - k) % 7;
		const m = Math.floor((a + 11 * h + 22 * l) / 451);
		const month = Math.floor((h + l - 7 * m + 114) / 31);
		const day = ((h + l - 7 * m + 114) % 31) + 1;
		return new Date(year, month - 1, day);
	}
}
